import React, { useState, useEffect, useRef, useCallback } from "react";
import { Paperclip, Smiley, Phone, MagnifyingGlass as Search, ArrowLeft, PaperPlane as Send } from "phosphor-react";
import Picker from "@emoji-mart/react";
import data from "@emoji-mart/data";
import { buildApiUrl, WEBSOCKET_URL } from "./config";

const WebSocketChat = ({ user, onClose }) => {
  // State management
  const [ws, setWs] = useState(null);
  const [connected, setConnected] = useState(false);
  const [conversations, setConversations] = useState([]);
  const [activeConversation, setActiveConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [contacts, setContacts] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [uploading, setUploading] = useState(false);
  
  // Refs
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);
  const emojiPickerRef = useRef(null);
  
  // WebSocket connection
  const connectWebSocket = useCallback(() => {
    if (!user?.id) return;
    
    console.log("🔌 [MOBILE DEBUG] Connecting to WebSocket:", `${WEBSOCKET_URL}/ws/chat/${user.id}?role=${user.role}`);
    console.log("🔌 [MOBILE DEBUG] WEBSOCKET_URL from config:", WEBSOCKET_URL);
    const wsUrl = `${WEBSOCKET_URL}/ws/chat/${user.id}?role=${user.role}`;
    const websocket = new WebSocket(wsUrl);
    
    websocket.onopen = () => {
      console.log("✅ [MOBILE DEBUG] WebSocket connected successfully to:", wsUrl);
      console.log("✅ [MOBILE DEBUG] WebSocket ready state:", websocket.readyState);
      setConnected(true);
    };
    
    websocket.onmessage = (event) => {
      console.log("📨 [MOBILE DEBUG] WebSocket message received:", event.data);
      const data = JSON.parse(event.data);
      
      if (data.type === "message_sent") {
        // Message confirmation from server
        console.log("✅ Message sent confirmation");
      } else if (data.sender && data.receiver) {
        // New incoming message
        handleIncomingMessage(data);
      } else if (data.error) {
        console.error("❌ WebSocket error:", data.error);
      }
    };
    
    websocket.onclose = (event) => {
      console.log("❌ [MOBILE DEBUG] WebSocket disconnected:", {
        code: event.code,
        reason: event.reason,
        wasClean: event.wasClean,
        url: wsUrl
      });
      setConnected(false);
      // Attempt to reconnect after 3 seconds
      setTimeout(connectWebSocket, 3000);
    };
    
    websocket.onerror = (error) => {
      console.error("❌ [MOBILE DEBUG] WebSocket error:", error);
      console.error("❌ [MOBILE DEBUG] WebSocket URL was:", wsUrl);
      console.error("❌ [MOBILE DEBUG] WebSocket state:", websocket.readyState);
    };
    
    setWs(websocket);
  }, [user?.id, user?.role]);
  
  // Handle incoming messages
  const handleIncomingMessage = (message) => {
    const conversationPartner = message.sender === user.id ? message.receiver : message.sender;
    
    // Update messages if this is the active conversation
    if (activeConversation === conversationPartner) {
      setMessages(prev => [...prev, message]);
      scrollToBottom();
      markAsRead(conversationPartner);
    }
    
    // Always update conversations list to show latest message and reorder
    setConversations(prev => {
      const updated = prev.map(conv => {
        if (conv.user_id === conversationPartner) {
          return {
            ...conv,
            unread_count: activeConversation === conversationPartner ? 0 : (conv.unread_count || 0) + 1,
            last_message: message,
            last_activity: message.timestamp
          };
        }
        return conv;
      });
      
      // Sort by last activity timestamp (most recent first)
      return updated.sort((a, b) => {
        const dateA = new Date(a.last_activity);
        const dateB = new Date(b.last_activity);
        return dateB.getTime() - dateA.getTime();
      });
    });
  };
  
  // Send message via WebSocket
  const sendMessage = () => {
    console.log("🚀 [MOBILE DEBUG] Attempting to send message:", {
      ws: !!ws,
      connected: connected,
      message: newMessage.trim(),
      activeConversation: activeConversation,
      wsReadyState: ws?.readyState,
      wsUrl: ws?.url
    });

    if (!ws || !connected || !newMessage.trim() || !activeConversation) {
      console.warn("❌ [MOBILE DEBUG] Cannot send message:", {
        "WebSocket exists": !!ws,
        "WebSocket ready state": ws?.readyState,
        "Connected": connected,
        "Has message": !!newMessage.trim(),
        "Message content": newMessage,
        "Has conversation": !!activeConversation,
        "Conversation ID": activeConversation
      });
      return;
    }
    
    const messageData = {
      to: activeConversation,
      content: newMessage,
      type: "text"
    };
    
    console.log("📤 [MOBILE DEBUG] Sending message data:", messageData);
    
    // Send via WebSocket
    try {
      ws.send(JSON.stringify(messageData));
      console.log("✅ [MOBILE DEBUG] Message sent successfully via WebSocket");
    } catch (error) {
      console.error("❌ [MOBILE DEBUG] Failed to send message via WebSocket:", error);
      console.error("❌ [MOBILE DEBUG] WebSocket state:", ws?.readyState);
      return;
    }
    
    // Add to local messages optimistically
    const optimisticMessage = {
      id: Date.now().toString(),
      sender: user.id,
      receiver: activeConversation,
      content: newMessage,
      timestamp: new Date().toISOString(),
      type: "text"
    };
    
    setMessages(prev => [...prev, optimisticMessage]);
    setNewMessage("");
    scrollToBottom();
    
    // Update conversation list
    updateConversationLastMessage(activeConversation, optimisticMessage);
  };
  
  // Update last message in conversation list
  const updateConversationLastMessage = (partnerId, message) => {
    setConversations(prev => {
      const updated = prev.map(conv => {
        if (conv.user_id === partnerId) {
          return {
            ...conv,
            last_message: message,
            last_activity: message.timestamp
          };
        }
        return conv;
      });
      
      // Sort by last activity timestamp (most recent first)
      return updated.sort((a, b) => {
        const dateA = new Date(a.last_activity);
        const dateB = new Date(b.last_activity);
        return dateB.getTime() - dateA.getTime();
      });
    });
  };
  
  // Load conversations from API
  const loadConversations = async () => {
    try {
      const response = await fetch(buildApiUrl(`/api/chat/conversations/${user.id}`));
      const data = await response.json();
      // Sort conversations by last activity (most recent first)
      const sortedConversations = (data.conversations || []).sort((a, b) => 
        new Date(b.last_activity) - new Date(a.last_activity)
      );
      setConversations(sortedConversations);
    } catch (error) {
      console.error("Failed to load conversations:", error);
    }
  };
  
  // Load contacts from API
  const loadContacts = async () => {
    try {
      const response = await fetch(buildApiUrl(`/api/chat/contacts/${user.id}/${user.role}`));
      const data = await response.json();
      setContacts(data.contacts || []);
    } catch (error) {
      console.error("Failed to load contacts:", error);
    }
  };
  
  // Load messages for active conversation
  const loadMessages = async (partnerId) => {
    try {
      const response = await fetch(buildApiUrl(`/api/chat/messages/${user.id}/${partnerId}`));
      const data = await response.json();
      setMessages(data.messages || []);
      scrollToBottom();
    } catch (error) {
      console.error("Failed to load messages:", error);
    }
  };
  
  // Mark conversation as read
  const markAsRead = (partnerId) => {
    setConversations(prev => 
      prev.map(conv => {
        if (conv.user_id === partnerId) {
          return { ...conv, unread_count: 0 };
        }
        return conv;
      })
    );
  };
  
  // Start conversation
  const startConversation = (contact) => {
    setActiveConversation(contact.id);
    loadMessages(contact.id);
    markAsRead(contact.id);
    
    // Add to conversations if not already there
    const existingConv = conversations.find(conv => conv.user_id === contact.id);
    if (!existingConv) {
      const newConversation = {
        user_id: contact.id,
        last_message: null,
        unread_count: 0,
        last_activity: new Date().toISOString(),
        contact_info: contact
      };
      setConversations(prev => [newConversation, ...prev]);
    }
  };
  
  // Handle file upload
  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;
    
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("sender_id", user.id);
      formData.append("sender_role", user.role);
      formData.append("receiver_id", activeConversation);
      formData.append("receiver_role", user.role === "parent" ? "teacher" : "parent");
      if (user.student_id) {
        formData.append("student_id", user.student_id);
      }
      
      const response = await fetch(buildApiUrl("/api/chat/upload"), {
        method: "POST",
        body: formData
      });
      
      const result = await response.json();
      
      // File upload will automatically create a chat message in the database
      // Refresh messages to show the new attachment message
      if (activeConversation) {
        await loadMessages(activeConversation);
        // Also refresh conversations to update last message
        await loadConversations();
      }
      
    } catch (error) {
      console.error("File upload failed:", error);
    } finally {
      setUploading(false);
      event.target.value = "";
    }
  };
  
  // Scroll to bottom
  const scrollToBottom = () => {
    setTimeout(() => {
      messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }, 100);
  };
  
  // Initialize
  useEffect(() => {
    if (user?.id) {
      connectWebSocket();
      loadConversations();
      loadContacts();
    }
    
    return () => {
      if (ws) {
        ws.close();
      }
    };
  }, [user?.id, connectWebSocket]);
  
  // Click outside emoji picker
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (emojiPickerRef.current && !emojiPickerRef.current.contains(event.target)) {
        setShowEmojiPicker(false);
      }
    };
    
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);
  
  // Keyboard shortcuts
  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };
  
  // Filter contacts for search
  const filteredContacts = contacts.filter(contact =>
    contact.name.toLowerCase().includes(searchTerm.toLowerCase())
  );
  
  // Render message
  const renderMessage = (message, index) => {
    const isOwn = message.sender === user.id;
    const showTime = index === 0 || 
      new Date(message.timestamp).getTime() - new Date(messages[index-1].timestamp).getTime() > 300000; // 5 mins
    
    return (
      <div key={message.id || index} className={`mb-4 ${isOwn ? 'text-right' : 'text-left'}`}>
        {showTime && (
          <div className="text-xs text-gray-500 mb-2 text-center">
            {new Date(message.timestamp).toLocaleString()}
          </div>
        )}
        <div className={`inline-block max-w-xs lg:max-w-md px-4 py-2 rounded-lg ${
          isOwn ? 'bg-green-500 text-white' : 'bg-gray-200 text-gray-900'
        }`}>
          {message.type === "image" && message.attachment ? (
            <div>
              <img 
                src={buildApiUrl(message.attachment)} 
                alt="Shared image" 
                className="max-w-full h-auto rounded mb-2 cursor-pointer"
                onClick={() => window.open(buildApiUrl(message.attachment), '_blank')}
              />
              <p className="text-sm">{message.content}</p>
            </div>
          ) : message.type === "file" && message.attachment ? (
            <div>
              <a 
                href={buildApiUrl(message.attachment)} 
                target="_blank" 
                rel="noopener noreferrer"
                className={`${isOwn ? 'text-blue-100 hover:text-white' : 'text-blue-600 hover:text-blue-800'} underline cursor-pointer block`}
                download={message.attachment_filename || message.content.replace('📎 ', '')}
              >
                {message.content}
              </a>
            </div>
          ) : (
            <p>{message.content}</p>
          )}
        </div>
      </div>
    );
  };
  
  if (!activeConversation) {
    // Conversation list view
    return (
      <div className="flex h-full bg-gradient-to-br from-slate-50 to-blue-50">
        {/* Sidebar */}
        <div className="w-80 bg-gradient-to-b from-white to-slate-50 border-r border-slate-200">
          {/* Header */}
          <div className="p-4 bg-white border-b">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold">Messages</h2>
              <button
                onClick={onClose}
                className="p-2 hover:bg-gray-100 rounded-full"
              >
                ×
              </button>
            </div>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
              <input
                type="text"
                placeholder="Search contacts..."
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>
          
          {/* Connection status */}
          <div className={`px-4 py-2 text-sm ${connected ? 'text-green-600' : 'text-red-600'}`}>
            {connected ? '🟢 Connected' : '🔴 Disconnected'}
          </div>
          
          {/* Conversations */}
          <div className="overflow-y-auto">
            {conversations.map(conv => (
              <div
                key={conv.user_id}
                onClick={() => startConversation({ id: conv.user_id, name: conv.contact_info?.name || conv.user_id })}
                className="p-4 border-b hover:bg-gray-100 cursor-pointer"
              >
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <div className="font-medium">{conv.contact_info?.name || conv.user_id}</div>
                    <div className="text-sm text-gray-500 truncate">
                      {conv.last_message?.content || "No messages yet"}
                    </div>
                  </div>
                  <div className="text-right">
                    {conv.unread_count > 0 && (
                      <span className="bg-green-500 text-white text-xs rounded-full px-2 py-1">
                        {conv.unread_count}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            ))}
            
            {/* New contacts */}
            {filteredContacts.map(contact => {
              const hasConversation = conversations.some(conv => conv.user_id === contact.id);
              if (hasConversation) return null;
              
              return (
                <div
                  key={contact.id}
                  onClick={() => startConversation(contact)}
                  className="p-4 border-b hover:bg-gray-100 cursor-pointer"
                >
                  <div className="font-medium">{contact.name}</div>
                  <div className="text-sm text-gray-500">{contact.role}</div>
                </div>
              );
            })}
          </div>
        </div>
        
        {/* Empty state */}
        <div className="flex-1 flex items-center justify-center bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50">
          <div className="text-center">
            <div className="text-6xl mb-4">💬</div>
            <h3 className="text-xl font-semibold mb-2">Select a conversation</h3>
            <p className="text-gray-500">Choose a contact from the sidebar to start chatting</p>
          </div>
        </div>
      </div>
    );
  }
  
  // Chat view
  const activeContact = contacts.find(c => c.id === activeConversation) || 
                        conversations.find(c => c.user_id === activeConversation);
  
  return (
    <div className="flex h-full bg-gradient-to-br from-slate-50 to-blue-50">
      {/* Chat Area */}
      <div className="flex-1 flex flex-col">
        {/* Header */}
        <div className="p-4 bg-white/90 backdrop-blur-sm border-b border-slate-200 flex items-center justify-between">
          <div className="flex items-center">
            <button
              onClick={() => setActiveConversation(null)}
              className="p-2 hover:bg-gray-100 rounded-full mr-3"
            >
              <ArrowLeft size={20} />
            </button>
            <div>
              <h3 className="font-semibold">
                {activeContact?.name || activeContact?.contact_info?.name || activeConversation}
              </h3>
              <p className="text-sm text-gray-500">
                {connected ? 'Online' : 'Offline'}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-full"
          >
            ×
          </button>
        </div>
        
        {/* Messages */}
        <div className="flex-1 overflow-y-auto p-4 bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50" style={{
          backgroundImage: `linear-gradient(135deg, #f5f7fa 0%, #e8eaf6 25%, #f3e5f5 50%, #fce4ec 75%, #f5f7fa 100%), 
                            url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='60' height='60' viewBox='0 0 60 60'%3E%3Cg fill-opacity='0.03'%3E%3Ccircle fill='%239C27B0' cx='30' cy='30' r='2'/%3E%3C/g%3E%3C/svg%3E")`
        }}>
          {messages.length === 0 ? (
            <div className="text-center text-gray-500 mt-20">
              <div className="text-4xl mb-4">👋</div>
              <p>Start the conversation!</p>
            </div>
          ) : (
            messages.map((message, index) => renderMessage(message, index))
          )}
          <div ref={messagesEndRef} />
        </div>
        
        {/* Input */}
        <div className="p-4 bg-white/90 backdrop-blur-sm border-t border-slate-200">
          <form 
            onSubmit={(e) => {
              console.log("📝 [MOBILE DEBUG] Form submitted");
              e.preventDefault();
              sendMessage();
            }}
            className="flex items-center space-x-2"
          >
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="p-2 hover:bg-gray-100 rounded-full"
              disabled={uploading}
            >
              <Paperclip size={20} />
            </button>
            <button
              type="button"
              onClick={() => setShowEmojiPicker(!showEmojiPicker)}
              className="p-2 hover:bg-gray-100 rounded-full"
            >
              <Smiley size={20} />
            </button>
            <div className="flex-1 relative">
              <textarea
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    console.log("⌨️ [MOBILE DEBUG] Enter key pressed");
                    e.preventDefault();
                    sendMessage();
                  }
                }}
                placeholder="Type a message..."
                className="w-full px-4 py-2 border border-gray-300 rounded-lg resize-none focus:outline-none focus:border-blue-500"
                rows={1}
                disabled={!connected}
              />
            </div>
            <button
              type="submit"
              onClick={(e) => {
                console.log("🖱️ [MOBILE DEBUG] Send button clicked");
                e.preventDefault();
                sendMessage();
              }}
              onTouchEnd={(e) => {
                console.log("👆 [MOBILE DEBUG] Send button touch ended");
                e.preventDefault();
                e.stopPropagation();
                if (!newMessage.trim() || !connected || uploading) {
                  console.log("❌ [MOBILE DEBUG] Touch send blocked - invalid state");
                  return;
                }
                console.log("🚀 [MOBILE DEBUG] Sending message via touch");
                sendMessage();
              }}
              disabled={!newMessage.trim() || !connected || uploading}
              className="p-2 bg-green-500 text-white rounded-full hover:bg-green-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              style={{ minWidth: '44px', minHeight: '44px' }} // Better touch target size
            >
              <Send size={20} />
            </button>
          </form>
          
          {/* Emoji Picker */}
          {showEmojiPicker && (
            <div ref={emojiPickerRef} className="absolute bottom-16 right-4 z-50">
              <Picker
                data={data}
                onEmojiSelect={(emoji) => {
                  setNewMessage(prev => prev + emoji.native);
                  setShowEmojiPicker(false);
                }}
                theme="light"
              />
            </div>
          )}
          
          {/* Hidden file input */}
          <input
            ref={fileInputRef}
            type="file"
            onChange={handleFileUpload}
            className="hidden"
            accept="image/*,.pdf,.doc,.docx,.txt"
          />
          
          {uploading && (
            <div className="mt-2 text-sm text-gray-500">
              📎 Uploading file...
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default WebSocketChat;
